import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { appResource } from '@jarvis.oddle/core/env'
import { findItemFromMenu, status } from '@jarvis.oddle/core/tools/product'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { generateImageSources } from '@jarvis.oddle/core/tools/uploadcare'
import { isEmpty, stripHTMLChar } from '@jarvis.oddle/core/tools/string'
import { isObjectEmpty } from '@jarvis.oddle/core/tools/object'
import { getDisplayPriceFromItem } from 'src/utils/product'

import { origin } from 'config/universal'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { selectMenuProductList } from 'src/redux/menu'
import { useAppContext } from 'src/containers/AppContext'

const Microtag = ({ currentUrl }: { currentUrl?: string }): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const router = useRouter()
  const { getProductImagePath, getLogoImagePath } = useAppContext()
  const { productId } = router.query
  const meta = useAppSelector(selectDetailMenuSite)
  const menu = useAppSelector(selectMenuProductList).data
  const product = findItemFromMenu({ menu, id: productId })

  if (isEmpty(productId) || isObjectEmpty(product)) {
    return null
  }

  const category = menu.find((c) => c.items.includes(product))

  const { src: logoImage } = generateImageSources({
    url: getLogoImagePath(),
    baseW: 60
  })
  const imageUrl = product.imageFullUrls?.[0] || product.imageUrls?.[0]
  const image = imageUrl
    ? getProductImagePath(imageUrl,
      true,
      {
        shouldCache: true,
        containerWidth: 500,
        containerHeight: 500
      })
    : null
  const ogImage =
    image || logoImage || `${origin()}${appResource('/favicon.ico')}`
  const description = product.description
    ? stripHTMLChar(product.description).substr(0, 150)
    : 'No description'
  const { displayPrice: priceAmount } = getDisplayPriceFromItem(t, product)
  const menuName = (meta || {}).menu.name
  const currencyCode = (meta || {}).currencyCode

  return (
    <Head>
      <title>{product.name}</title>
      <meta itemProp='name' content={product.name} />
      <meta itemProp='description' content={description} />
      <meta key='og:title' property='og:title' content={product.name} />
      <meta
        key='og:description'
        property='og:description'
        content={description}
      />
      {currentUrl && (
        <meta key='og:url' property='og:url' content={`https:${currentUrl}`} />
      )}
      <meta key='product:brand' property='product:brand' content={menuName} />
      <meta
        key='product:availability'
        property='product:availability'
        content={
          product.status === status.AVAILABLE ? 'in stock' : 'out of stock'
        }
      />
      <meta
        key='product:condition'
        property='product:condition'
        content='new'
      />
      <meta
        key='product:price:amount'
        property='product:price:amount'
        content={priceAmount}
      />
      <meta
        key='product:price:currency'
        property='product:price:currency'
        content={currencyCode}
      />
      <meta
        key='product:retailer_item_id'
        property='product:retailer_item_id'
        content={product.id}
      />
      <meta
        key='product:custom_label_0'
        property='product:custom_label_0'
        content={category.name}
      />
      <meta key='twitter:title' name='twitter:title' content={product.name} />
      <meta
        key='twitter:description'
        name='twitter:description'
        content={description}
      />
      <meta key='twitter:card' name='twitter:card' content='summary' />
      {ogImage && <meta key='image' property='image' content={ogImage} />}
      {ogImage && <meta key='og:image' property='og:image' content={ogImage} />}
      {ogImage && (
        <meta key='twitter:image' name='twitter:image' content={ogImage} />
      )}
    </Head>
  )
}

export default Microtag
